const REDACTED = "[REDACTED]";

const SENSITIVE_KEY_PATTERNS = [
  /api[-_]?key/i,
  /app[-_]?key/i,
  /authorization/i,
  /cookie/i,
  /password/i,
  /secret/i,
  /token/i,
  /nonce/i,
  /cvv/i,
  /cvc/i,
  /security[-_]?code/i,
  /signature/i,
  /account[-_]?number/i,
  /routing[-_]?number/i,
  /card[-_]?number/i,
  /cc[-_]?account/i,
  /credit[-_]?card[-_]?account/i,
];

type RedactionOptions = {
  maxDepth?: number;
  maxArrayItems?: number;
  maxStringLength?: number;
};

function shouldRedactKey(key: string): boolean {
  return SENSITIVE_KEY_PATTERNS.some((pattern) => pattern.test(key));
}

function trimLongString(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value;
  return `${value.slice(0, maxLength)}... [${value.length - maxLength} more chars]`;
}

export function redactOnlineJoinDebugValue(
  value: unknown,
  options: RedactionOptions = {},
  depth = 0,
): unknown {
  const maxDepth = options.maxDepth ?? 7;
  const maxArrayItems = options.maxArrayItems ?? 8;
  const maxStringLength = options.maxStringLength ?? 600;

  if (value == null) return value;
  if (typeof value === "string") return trimLongString(value, maxStringLength);
  if (typeof value !== "object") return value;
  if (value instanceof Date) return value.toISOString();
  if (depth >= maxDepth) return "[Max depth reached]";

  if (Array.isArray(value)) {
    const visibleItems = value
      .slice(0, maxArrayItems)
      .map((item) =>
        redactOnlineJoinDebugValue(item, options, depth + 1),
      );
    if (value.length > maxArrayItems) {
      visibleItems.push(`[${value.length - maxArrayItems} more items]`);
    }
    return visibleItems;
  }

  const result: Record<string, unknown> = {};
  for (const [key, nestedValue] of Object.entries(value as Record<string, unknown>)) {
    result[key] = shouldRedactKey(key)
      ? REDACTED
      : redactOnlineJoinDebugValue(nestedValue, options, depth + 1);
  }
  return result;
}

export function parseAndRedactOnlineJoinDebugJson(
  rawValue: unknown,
  options?: RedactionOptions,
): unknown {
  if (typeof rawValue !== "string") {
    return redactOnlineJoinDebugValue(rawValue, options);
  }

  try {
    return redactOnlineJoinDebugValue(JSON.parse(rawValue), options);
  } catch {
    return redactOnlineJoinDebugValue(rawValue, options);
  }
}
